import * as React from 'react'

import Bugsnag, { FeatureFlag, NotifiableError } from '@bugsnag/js'

import { initializeBugsnag } from './lib/bugsnag-runtime'

const generateErrorMessage = (message: NotifiableError | string) => {
  return typeof message === 'string' ? new Error(message) : message
}

const notify = (message: NotifiableError | string, severity: 'info' | 'warning' | 'error') => {
  const errorMessage = generateErrorMessage(message)
  if (process.env['NODE_ENV'] === 'development') {
    const method = severity === 'warning' ? 'warn' : severity
    // eslint-disable-next-line no-console
    console[method](message)
  }
  Bugsnag.notify(errorMessage, function (event) {
    event.severity = severity
  })
}

type LoggableFlagPrimitives = string | number | boolean | null | undefined
type LoggableFlagValues =
  | LoggableFlagPrimitives
  | { [key: string]: LoggableFlagValues }
  | Readonly<{ [key: string]: LoggableFlagValues }>
  | Array<LoggableFlagValues>
  | ReadonlyArray<LoggableFlagValues>

const logger = {
  /*
   * Initializes Bugsnag error monitoring
   * Put this at the highest level of your application to capture errors for monitoring and alerting.
   * This is also required for the Bugsnag methods below to work
   */
  initErrorMonitoring: () => {
    initializeBugsnag()
  },
  /*
   * Creates a React <ErrorBoundary /> component to capture errors and renders an error state UI.
   * It only captures methods in the component tree that occur when rendering, in lifecycle methods,
   * and in constructors.
   */
  createErrorBoundary: () => {
    return Bugsnag.getPlugin?.('react')?.createErrorBoundary(React)
  },
  /*
   * Captures errors for monitoring and alerting
   */
  error: (message: NotifiableError | string) => {
    notify(message, 'error')
  },
  /*
   * Logs errors with severity: info for monitoring and alerting
   */
  info: (message: NotifiableError | string) => {
    notify(message, 'info')
  },
  /*
   * Logs errors with severity: warning for monitoring and alerting
   */
  warn: (message: NotifiableError | string) => {
    notify(message, 'warning')
  },
  /**
   * Register features flags for session
   */
  addFeatureFlags: (
    featureFlags: Array<{
      flag: string
      value: LoggableFlagValues
    }>
  ) => {
    const processedFlags: Array<FeatureFlag> = featureFlags.map(({ flag, value }) => {
      let variant: FeatureFlag['variant']

      if (typeof value === 'string') {
        variant = value
      } else if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'object') {
        variant = JSON.stringify(value)
      } else if (value === undefined) {
        variant = 'undefined'
      } else {
        variant = value
      }

      return { name: flag, variant }
    })

    Bugsnag.addFeatureFlags(processedFlags)
  },
  /**
   * Clears set feature flags for session
   */
  clearFeatureFlags: () => {
    Bugsnag.clearFeatureFlags()
  },
  /**
   * Register metadata for session
   */
  addMetadata: (field: string, metadata: Record<string, unknown>) => {
    Bugsnag.addMetadata(field, metadata)
  },
  /**
   * Clears set metadata field for session
   */
  clearMetadata: (field: string) => {
    Bugsnag.clearMetadata(field)
  },
}

export default logger
